/**
 * WHERE THESE SCREENS GO, as data rather than as a router.
 *
 * `@tanstack/react-router` IS RULED (adr/0007) and was not installed when this file was written, so
 * the screens are plain components and this file is the list a router consumes in one pass. The
 * router has since landed (`src/router.tsx`) and does exactly that, and the sidebar reads `label`
 * from this same list (`features/shell/cabinet-navigation.ts`). Wiring them is:
 *
 *   CABINET_ROUTES.map(({ path, Component }) => createRoute({ path, component: Component }))
 *
 * ROUTES AND LABELS ARE OURS AND ARE NOT THE OWNER'S TO RULE — decision-log rows 99, 100 and 108,
 * restated at 09-authors-app.md: "layout, wording, navigation and screen flow are the app's
 * own business, settled in the build and never brought to the owner". The ONE route the corpus
 * fixes anywhere is `/enter`, and it is not one of these.
 *
 * The paths below follow the prototype's own nav labels where nothing argues otherwise (CAB_NAV:
 * Overview, Articles, Dashboard, Comments, Chats, Payments, Calendar & Ideas, Author Profile,
 * Support), with two departures: the "Dashboard" tabs are Overview plus Statistics, and Comments
 * and "Chats" are one "Inbox" (design 24, T17). The account's own screens follow Calendar.
 */
import { DashboardRoute } from '../../routes/dashboard/DashboardRoute';
import { ArticlesRoute } from '../../routes/articles/ArticlesRoute';
import { ArticleStatsRoute } from '../../routes/articles/ArticleStatsRoute';
import { EditorRoute } from '../../routes/articles/EditorRoute';
import { VideoEditorRoute } from '../../routes/videos/VideoEditorRoute';
import { VIDEO_EDITOR_ROUTE_PATH } from '../../routes/videos/paths';
import {
  CommentsInboxRoute,
  INBOX_PATH,
  InboxRoute,
  MessagesInboxRoute,
  NotificationsInboxRoute,
} from '../../routes/inbox';
import { PaymentsRoute } from '../../routes/payments/PaymentsRoute';
import { CalendarRoute } from '../../routes/calendar/CalendarRoute';
import { PAYOUT_METHOD_PATH, PayoutMethodRoute } from '../../routes/payments/PayoutMethodRoute';
import { SETTINGS_PATH, SettingsRoute } from '../../routes/settings/SettingsRoute';
import * as notifications from '../../routes/notifications';
import { MESSAGES_PATH } from '../../routes/messages';
import type { FunctionComponent } from 'react';

export interface CabinetRoute {
  readonly path: string;
  /** What the nav calls it. */
  readonly label: string;
  /** One line for a nav tooltip or a page description. */
  readonly summary: string;
  /** The authors-api operations this screen calls. Kept here so the coverage is countable. */
  readonly operations: readonly string[];
  /**
   * A FUNCTION component: `@tanstack/react-router`'s `RouteComponent` refuses a class component,
   * so the constraint is stated here rather than cast away in `router.tsx` (narrowed 2026-09-03).
   */
  readonly Component: FunctionComponent;
  /** `false`: reached from another screen, not the sidebar, which lights that parent instead. */
  readonly inSidebar?: false;
}

export const CABINET_ROUTES: readonly CabinetRoute[] = [
  {
    path: '/dashboard',
    label: 'Overview',
    summary: 'The tile set, plus the running figure for the month that is still open.',
    operations: overviewOperations(),
    Component: DashboardRoute,
  },
  {
    path: '/articles',
    label: 'Articles',
    summary: 'Everything you have written, and every move you can make on it.',
    operations: [
      'listOwnArticles',
      'createDraft',
      // v1.0.29: "+ New" starts a video, or asks staff for video access - and (1.2.0) a live
      // stream, by `createVideoDraft` too, or asks for stream access.
      'createVideoDraft',
      'requestVideoAccess',
      'requestStreamAccess',
      // ...and each video card carries its own figures.
      'listVideoStats',
      'withdrawArticle',
      'pullScheduledArticle',
      'archiveOwnArticle',
      'unarchiveOwnArticle',
      'deleteNeverPublishedDraft',
    ],
    Component: ArticlesRoute,
  },
  {
    // `$articleId` is TanStack Router's path-parameter spelling, handed straight to `createRoute`,
    // so the descriptor and the router agree. It is reached from a row, never from the sidebar.
    // ★ ROUTE-ORDER TRAP: `/articles/statistics` (two segments) and this (three) cannot collide,
    // but a later `/articles/$articleId` at two segments WOULD shadow `/articles/statistics`.
    path: '/articles/$articleId/edit',
    label: 'Editor',
    summary: 'Write, see what still has to be fixed, and answer an editor\u2019s notes.',
    operations: [
      'getOwnArticle',
      'autosaveArticle',
      'submitArticle',
      'resubmitArticle',
      'getStructuralRules',
      'listEditorCategories',
      'suggestTags',
      'getPublishDateSuggestions',
      'checkArticleSlugAvailability',
      'claimArticleImageUpload',
      // 1.3.0: an article's video blocks.
      'getArticleVideoBlock',
      'setArticleVideoBlock',
      'mintDraftPreviewLink',
      'getArticleReview',
      'resolveReviewFinding',
      'listArticleCalendarEvents',
      'setArticleCalendarEvents',
      'getAuthorCalendar',
      'listArticleVersions',
      'getArticleVersion',
      'saveArticleVersion',
      'getOwnProfile', 'listSourceLanguages',
      'getArticleSummary',
    ],
    Component: EditorRoute,
  },
  {
    path: '/articles/statistics',
    label: 'Statistics',
    summary: 'How each article did, and who your readers were, one sealed month at a time.',
    operations: ['listArticleStats', 'getDashboard', 'getAudienceBreakdown'],
    Component: ArticleStatsRoute,
  },
  videoEditorRoute(),
  // THE INBOX (design 24, T17): the comments and the messages readers send (register R-006) as two
  // tabs of one sidebar entry, beside a third for the MoonX team - and the two screens' old
  // addresses, which open their tabs. All in `readerRoutes()` below, spread here. WHY A SPREAD:
  // the calendar entry and `CABINET_OPERATIONS_COVERED` below are cited by number from other
  // files, and this comment and the spread take exactly the seven lines the Comments entry once
  // took, so neither moved.
  ...readerRoutes(),
  {
    path: '/payments',
    label: 'Payments',
    summary: 'Your balance, this month so far, your statements and where money goes.',
    operations: ['getPaymentsView', 'listStatements', 'getStatement', 'getPayoutMethod', 'listEarlierMonths'],
    Component: PaymentsRoute,
  },
  {
    path: '/calendar',
    // "Ideas" is dropped from the prototype's "Calendar & Ideas" on purpose: its three panes are
    // PLATFORM WORK OWED, and a label promising them would promise panes that are not there.
    label: 'Calendar',
    summary: 'The sky, your own pieces, and how busy each day already is across MoonX.',
    operations: ['getAuthorCalendar'],
    Component: CalendarRoute,
  },
  ...accountRoutes(),
];

/** Every authors-api operation these screens reach, deduplicated. Fifty-nine since design 25. */
export const CABINET_OPERATIONS_COVERED: readonly string[] = [
  ...new Set(CABINET_ROUTES.flatMap((route) => route.operations)),
].sort();

/**
 * THE ACCOUNT'S OWN SCREENS (register R-005, R-015, R-016): where money goes, the notification feed
 * and its switches, and the settings. None is in the prototype's nav; Notifications and Settings go
 * after Calendar, and the two sub-screens are reached from their parents (`inSidebar: false`), so
 * the sidebar lights Payments on `/payments/method` and Notifications on `/notifications/preferences`.
 *
 * A FUNCTION DECLARATION, so it is hoisted above `CABINET_ROUTES`, which spreads it - a `const` here
 * would be read before it exists. It sits below the list, not inside it, because the lines above are
 * cited by number from other files (the calendar entry, `CABINET_OPERATIONS_COVERED`).
 */
function accountRoutes(): readonly CabinetRoute[] {
  return [
    {
      path: PAYOUT_METHOD_PATH,
      label: 'Payment details',
      summary: 'Who we pay, where the money goes and what you confirmed.',
      // The payout documents, the countries, and the payout rule's figures (design 25).
      operations: [
        'getPayoutMethod',
        'updatePayoutMethod',
        'listAgreements',
        'acceptAgreements',
        'listCountries',
        'getPaymentsView',
      ],
      inSidebar: false,
      Component: PayoutMethodRoute,
    },
    {
      path: notifications.NOTIFICATIONS_PATH,
      label: 'Notifications',
      summary: 'Everything MoonX has told you, and what is still unread - a tab of the Inbox.',
      operations: ['listNotifications', 'markNotificationsRead'],
      inSidebar: false,
      Component: NotificationsInboxRoute,
    },
    {
      // THE PATH EVERY STATEMENT E-MAIL LINKS TO - see routes/notifications/paths.ts.
      path: notifications.NOTIFICATION_PREFERENCES_PATH,
      label: 'What you are sent',
      summary: 'Which notifications reach your phone and your e-mail, and which are always sent.',
      operations: ['getNotificationPreferences', 'updateNotificationPreferences'],
      inSidebar: false,
      Component: notifications.NotificationPreferencesRoute,
    },
    {
      path: SETTINGS_PATH,
      label: 'Settings',
      summary: 'Birth data and star signs, the public-earnings consent, and closing the account.',
      operations: [
        'getOwnProfile',
        'setBirthData', 'searchBirthPlaces',
        'clearBirthData',
        'setComputedSignsVisibility',
        'setEarningsVisibility',
        'setTipsVisibility',
        'requestAccountDeletion', 'getPaymentsView', 'getPlatformContacts',
      ],
      Component: SettingsRoute,
    },
  ];
}

/**
 * READERS TALKING TO THE AUTHOR: the comments on their articles, and the messages sent to them
 * directly (register R-006) - since design 24 (T17) the two tabs of one Inbox, beside a third for
 * the MoonX team. A function declaration for `accountRoutes`' reason - it is hoisted above
 * `CABINET_ROUTES`, which spreads it.
 *
 * ONE SIDEBAR ENTRY, THREE ADDRESSES. `/inbox` is the entry. `/comments` and `/messages` are the
 * two screens' old addresses, kept because the Overview, the statistics and old notifications link
 * to them: each draws the Inbox on its own tab, off the sidebar, and `SIDEBAR_PARENTS` below lights
 * "Inbox" there. Each keeps the operations its tab calls, so a link's screen stays countable.
 */
function readerRoutes(): readonly CabinetRoute[] {
  // Inside the function, not beside it: it runs while `CABINET_ROUTES` is being built, before any
  // `const` below it in this file exists.
  const comments = ['listCommentsOnMyArticles', 'replyToComment'];
  // `getOwnProfile`: the author's own language, which a translation is asked in.
  const messages = [
    'listSupporterMessages',
    'replyToSupporterMessage',
    'retractOwnReply',
    'unhideSupporterMessage',
    'requestSupporterMessageTranslation',
    'getOwnProfile',
  ];
  return [
    {
      path: INBOX_PATH,
      label: 'Inbox',
      summary:
        'What readers send you - comments and messages - what MoonX tells you, and how to reach the MoonX team.',
      operations: [
        ...comments,
        ...messages,
        'listNotifications',
        'markNotificationsRead',
        'getNotificationPreferences',
        'getPlatformContacts',
      ],
      Component: InboxRoute,
    },
    {
      path: '/comments',
      label: 'Comments',
      summary: 'What readers said on your articles, and your answers.',
      operations: comments,
      inSidebar: false,
      Component: CommentsInboxRoute,
    },
    {
      // LOCKED D44: "v1 cabinet keeps a minimal supporter-messages view (read + reply)".
      path: MESSAGES_PATH,
      label: 'Messages',
      summary: 'What readers wrote to you, with a tip or without, and your one answer to each.',
      operations: messages,
      inSidebar: false,
      Component: MessagesInboxRoute,
    },
  ];
}

/**
 * THE VIDEO EDITOR (v1.0.29): a video is a kind of article, reached from the Articles list's Videos,
 * so the sidebar lights Articles on it (`SIDEBAR_PARENTS`). A hoisted function for
 * `accountRoutes`' reason.
 */
function videoEditorRoute(): CabinetRoute {
  return {
    path: VIDEO_EDITOR_ROUTE_PATH,
    label: 'Video editor',
    summary: 'The video, its cover, a text description, and the rules every video keeps.',
    operations: [
      'getOwnArticle',
      'autosaveArticle',
      'submitArticle',
      'resubmitArticle',
      'getStructuralRules',
      'listEditorCategories',
      'suggestTags',
      'getPublishDateSuggestions',
      'claimArticleImageUpload',
      'getArticleReview',
      'resolveReviewFinding',
      'listArticleVersions',
      'listSourceLanguages',
      'getArticleVideo',
      'setArticleVideo',
      'removeArticleVideo',
      'setVideoCover',
      'setVideoDraftConfirmations',
      // 1.2.0: a live stream's planned times.
      'setStreamTimes',
    ],
    inSidebar: false,
    Component: VideoEditorRoute,
  };
}

/**
 * ADDRESSES OFF THE SIDEBAR WHOSE ENTRY IS NOT THEIR PATH'S PREFIX, and the entry lit on them. An
 * `inSidebar: false` screen under its parent's path (`/payments/method`) needs nothing here: the
 * sidebar's longest-prefix rule lights the parent (`features/shell/cabinet-navigation.ts`).
 */
export const SIDEBAR_PARENTS: ReadonlyMap<string, string> = new Map([
  ['/comments', INBOX_PATH],
  [MESSAGES_PATH, INBOX_PATH],
  // v1.0.14: Notifications is an Inbox tab; this also lights Inbox on /notifications/preferences.
  [notifications.NOTIFICATIONS_PATH, INBOX_PATH],
  // v1.0.29: the video editor is reached from the Articles list.
  ['/videos', '/articles'],
]);

/**
 * The Overview's reads (routes/dashboard): its tiles, the month's figure, what is waiting - and,
 * since design 25, Payment details for its reminder. A hoisted function for `accountRoutes`'
 * reason: the entry it fills is cited by number, and the list no longer fits on its line.
 */
function overviewOperations(): readonly string[] {
  return [
    'getDashboard',
    'getPaymentsView',
    'listOwnArticles',
    'listCommentsOnMyArticles',
    'getPayoutMethod',
  ];
}
